import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, ClipboardCheck, Award, Clock } from 'lucide-react';
import useOrganizationMemberActivityStore from '@/stores/AdminStore/OrganizationMemberActivityStore';
import { LoadingSpinner, ErrorDisplay } from '@/Components/common';
import CoursesTab from './tabs/CoursesTab';
import AssessmentsTab from './tabs/AssessmentsTab';
import CertificatesTab from './tabs/CertificatesTab';
import HoursTab from './tabs/HoursTab';
import './OrganizationMemberActivity.css';

type TabKey = 'courses' | 'assessments' | 'certificates' | 'hours';

const OrganizationMemberActivity: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { overview, loadingOverview, error, fetchOverview, reset } = useOrganizationMemberActivityStore();
  const [activeTab, setActiveTab] = useState<TabKey>('courses');

  useEffect(() => {
    if (!userId) return;
    fetchOverview(userId);
    return () => { reset(); };
  }, [userId, fetchOverview, reset]);

  if (!userId) {
    return <div className="omact-container"><ErrorDisplay title="Missing user id" message="No user id in URL" type="error" /></div>;
  }

  if (loadingOverview && !overview) {
    return <div className="omact-container"><LoadingSpinner size="lg" /></div>;
  }

  if (error && !overview) {
    return (
      <div className="omact-container">
        <ErrorDisplay
          title="Unable to load member"
          message={error}
          type="error"
          showRetry
          onRetry={() => fetchOverview(userId)}
        />
      </div>
    );
  }

  const profile = overview?.profile;
  const stats = overview?.stats || {};
  const fullName = profile ? `${profile.first_name} ${profile.last_name}`.trim() || profile.email : '';
  const initial = (profile?.first_name?.[0] || profile?.email?.[0] || '?').toUpperCase();

  const tabs: Array<{ key: TabKey; label: string; Icon: React.ComponentType<{ size?: number }> }> = [
    { key: 'courses', label: 'Courses', Icon: BookOpen },
    { key: 'assessments', label: 'Assessments', Icon: ClipboardCheck },
    { key: 'certificates', label: 'Certificates', Icon: Award },
    { key: 'hours', label: 'Hours & streak', Icon: Clock },
  ];

  return (
    <div className="omact-container">
      <button className="omact-back-btn" onClick={() => navigate('/admin/users')}>
        <ArrowLeft size={16} /> Back to organization users
      </button>

      <div className="omact-header">
        <div className="omact-avatar">{initial}</div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 600 }}>{fullName || 'Unknown user'}</div>
          <div className="omact-header-meta">{profile?.email}</div>
        </div>
      </div>

      <div className="omact-overview-grid">
        <div className="omact-stat-card">
          <div className="omact-stat-label">Total courses</div>
          <div className="omact-stat-value">{Number(stats.total_courses ?? 0)}</div>
        </div>
        <div className="omact-stat-card">
          <div className="omact-stat-label">Completed</div>
          <div className="omact-stat-value">{Number(stats.completed_courses ?? 0)}</div>
        </div>
        <div className="omact-stat-card">
          <div className="omact-stat-label">Certificates</div>
          <div className="omact-stat-value">{Number(stats.total_certificates ?? 0)}</div>
        </div>
        <div className="omact-stat-card">
          <div className="omact-stat-label">Streak</div>
          <div className="omact-stat-value">{Number(stats.learning_streak ?? 0)}d</div>
        </div>
      </div>

      <div className="omact-tabs">
        {tabs.map(({ key, label, Icon }) => (
          <button
            key={key}
            className={`omact-tab ${activeTab === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon size={16} /> {label}
            </span>
          </button>
        ))}
      </div>

      <div className="omact-tab-panel">
        {activeTab === 'courses' && <CoursesTab userId={userId} />}
        {activeTab === 'assessments' && <AssessmentsTab userId={userId} />}
        {activeTab === 'certificates' && <CertificatesTab userId={userId} />}
        {activeTab === 'hours' && <HoursTab userId={userId} />}
      </div>
    </div>
  );
};

export default OrganizationMemberActivity;
